import snakeCase from 'lodash/snakeCase';
import { isArray, isObject } from '~/utilities/obj';
import { isEmptyArray, isNotNil } from '~/utilities/type-guards';
import { liveStreamEventRoutes as Routes } from '~/singlePageApp/screens/LiveStreamScreen/liveStreamEventRoutes';

/**
 *
 * @param {Record<string, string>} [params = {}] - A set of key, value pairs to use as query params
 * @returns {string}
 */
const buildQueryParams = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return query.length ? `?`.concat(query) : query;
};

/**
 *
 * @param {string} hashedId - The hashed id of the live stream event
 * @param {boolean} isJoinKitEnabled - Whether the JoinKit feature flag is enabled
 * @param {string} [baseUrl = window.location.origin] - The base url for the host link
 * @returns {string}
 */
export const getHostLink = (hashedId, isJoinKitEnabled, baseUrl = window.location.origin) =>
  `${baseUrl}${isJoinKitEnabled ? Routes.hostJoinPage(hashedId) : Routes.hostView(hashedId)}`;

/**
 *
 * @param {string} hashedId - The hashed id of the live stream event
 * @param {Record<string, boolean | string>} [queryParams = {}] - A set of key, value pairs to use as query params
 * @param {string} [baseUrl = window.location.origin] - The base url for the audience link
 * @returns {string}
 */
export const getAudienceLink = (hashedId, queryParams = {}, baseUrl = window.location.origin) =>
  `${baseUrl}${Routes.audienceView(hashedId)}${buildQueryParams(queryParams)}`;

/**
 *
 * @param {string} hashedId - The hashed id of the live stream event
 * @param {string} joinKey - The join key for the live stream event
 * @param {boolean} isJoinKitEnabled - Whether the JoinKit feature flag is enabled
 * @param {string} [baseUrl = window.location.origin] - The base url for the panelist link
 * @returns {string}
 */
export const getPanelistLink = (
  hashedId,
  joinKey,
  isJoinKitEnabled,
  baseUrl = window.location.origin,
) =>
  `${baseUrl}${isJoinKitEnabled ? Routes.joinPage(hashedId, joinKey) : Routes.panelistView(hashedId, joinKey)}`;

/**
 *
 * @param {string} hashedId - The hashed id of the live stream event
 * @param {string} [baseUrl = window.location.origin] - The base url for the multiplex link
 * @returns {string}
 */
export const getMultiplexLink = (hashedId, baseUrl = window.location.origin) =>
  `${baseUrl}${Routes.multiplex(hashedId)}`;

/**
 * A simple userAgent lookup to check if devices is mobile.
 * TODO - Swap this function out for a solution using detect.js
 *
 * @returns {boolean}
 */
export const isMobile = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

/**
 * This transforms a plain js object into a FormData object
 * with snake_case keys, ready to be used as standard Rails params.
 * It uses a recursive approach to support nested objects.
 *
 * @param {object} data - Object to append to FormData
 * @param {object} [formData = new FormData] - The FormData instance
 * @param {string} [namespace=null] - The namespace for each value
 */
export const toFormData = (data, formData = new FormData(), namespace = null) => {
  Object.entries(data).forEach(([key, value]) => {
    // Format the parameter key
    const paramKey = isNotNil(namespace)
      ? `${snakeCase(namespace)}[${snakeCase(key)}]`
      : snakeCase(key);

    // To send an empty array, Rails expects an empty string.
    if (isArray(value)) {
      const array = isEmptyArray(value) ? [''] : value;
      array.forEach((item) => formData.append(`${paramKey}[]`, item));
      return;
    }

    // Re-run the function with nested object key/value pairs
    if (isObject(value)) {
      toFormData(value, formData, paramKey);
      return;
    }

    formData.append(paramKey, value);
  });

  return formData;
};
